import React, { useEffect, useState } from "react";
import {
    IonCard,
    IonCardContent,
    IonAlert,
    IonButton,
    IonRadioGroup,
    IonItem,
    IonRadio,
    IonLabel,
} from "@ionic/react";
import "./CountShapesFigure.css";

const shapeTypes = ["circle", "square", "triangle", "rectangle"];
const getRandomShape = () => shapeTypes[Math.floor(Math.random() * shapeTypes.length)];

type ShapeData = {
    type: string;
    top: number;
    left: number;
    size: number;
};

type RollSlideOption = {
    src: string;
    label: string;
    answerType: "roll" | "slide" | "both";
};

type ShapeName = 'cuboid' | 'cylinder' | 'cone' | 'sphere' | 'cube' | 'pyramid';


// Image arrays (use real URLs instead of placeholders)
const rollOnlyImages: RollSlideOption[] = [
    { src: "ball.png", label: "Ball", answerType: "roll" },
    { src: "egg.png", label: "Egg", answerType: "roll" },
    { src: "pen.png", label: "Pen", answerType: "roll" },
    { src: "earth.png", label: "Globe", answerType: "roll" },
    { src: "wheel.png", label: "Wheel", answerType: "roll" },
    { src: "rolling-pin.png", label: "Rolling Pin", answerType: "roll" },
];

const slideOnlyImages: RollSlideOption[] = [
    { src: "box.png", label: "Box", answerType: "slide" },
    { src: "book.png", label: "Book", answerType: "slide" },
    { src: "guiter.png", label: "Guiter", answerType: "slide" },
    { src: "batball.png", label: "Bat", answerType: "slide" },
    { src: "giftbox.png", label: "Gift Box", answerType: "slide" },
];

const bothImages: RollSlideOption[] = [
    { src: "sauce.png", label: "Sauce", answerType: "both" },
    { src: "bottle.png", label: "Bottle", answerType: "both" },
    { src: "drum.png", label: "Drum", answerType: "both" },
    { src: "jar.png", label: "Jar", answerType: "both" },
    { src: "gas-cylinder.png", label: "Gas Cylinder", answerType: "both" },
    { src: "can.png", label: "Can", answerType: "both" },
];

const shapesBackground: Record<ShapeName, string[]> = {
    "cuboid": ["eraser.png", "brick.png", "bookthick.png", "matchbox.png", "juicebox.png"],
    "cylinder": ["sauce.png", "bottle.png", "jar.png", "gas-cylinder.png", "can.png"],
    "cone": ["birthdaycap.png", "traffic-cones.png", "conetree.png", "conetoy.png", "ice-cream-cone.png"],
    "sphere": ["ball.png", "earth.png", "marble.png", "orangesphere.png", "sphereflower.png"],
    "cube": ["rubik.png", "giftbox.png", "dice.png", "numberbox.png", "colorbox.png"],
    "pyramid": ["pyramid.png", "tent.png", "watermelonpyramid.png", "mountain.png", "pyramid1.png"]
};

const generateOptions = (correct: number): number[] => {
    const options = new Set<number>();
    options.add(correct);
    while (options.size < 4) {
        const random = correct + Math.floor(Math.random() * 5) - 2;
        if (random >= 0 && random !== correct) {
            options.add(random);
        }
    }
    return Array.from(options).sort(() => 0.5 - Math.random());
};

const CountShapesFigure: React.FC = () => {
    const [questionType, setQuestionType] = useState<string>("");
    const [shapes, setShapes] = useState<ShapeData[]>([]);
    const [targetShape, setTargetShape] = useState<string>("");
    const [correctCount, setCorrectCount] = useState<number>(0);
    const [options, setOptions] = useState<number[]>([]);
    const [selected, setSelected] = useState<any>(null);
    const [showImage, setShowImage] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);

    const [rollSlideQuestion, setRollSlideQuestion] = useState<string>("");
    const [rollSlideOptions, setRollSlideOptions] = useState<RollSlideOption[]>([]);
    const [correctRollSlide, setCorrectRollSlide] = useState<string>("");

    const [looksLikeQuestion, setLooksLikeQuestion] = useState<string>("");
    const [looksLikeOptions, setLooksLikeOptions] = useState<{ label: string; src: string }[]>([]);
    const [correctLooksLike, setCorrectLooksLike] = useState<string>("");




    const questionTypes = ["figure", "rollSlide", "looksLike"];
    const getRandomQuestionType = () => questionTypes[Math.floor(Math.random() * 3)];

    const shapeCount = 10;

    useEffect(() => {
        const type = getRandomQuestionType();
        setQuestionType(type);

        if (type === "figure") {
            generateNewFigure();
        } else if (type === "rollSlide") {
            generateRollSlide();
        } else if (type === "looksLike") {
            generateLooksLike();
        }
    }, []);

    const generateLooksLike = () => {
        const shapeKeys: ShapeName[] = Object.keys(shapesBackground) as ShapeName[];

        const randomShape = shapeKeys[Math.floor(Math.random() * shapeKeys.length)];
        const correctImages = shapesBackground[randomShape];

        const correct = correctImages[Math.floor(Math.random() * correctImages.length)];

        // Get wrong images from other shapes
        const otherShapes = shapeKeys.filter(k => k !== randomShape);
        const wrongImages: string[] = [];
        while (wrongImages.length < 3) {
            const randomOtherShape = otherShapes[Math.floor(Math.random() * otherShapes.length)];
            const imgList = shapesBackground[randomOtherShape];
            const randomWrong = imgList[Math.floor(Math.random() * imgList.length)];
            if (!wrongImages.includes(randomWrong) && randomWrong !== correct) {
                wrongImages.push(randomWrong);
            }
        }

        const options = [
            { label: correct, src: correct },
            ...wrongImages.map(src => ({ label: src, src }))
        ].sort(() => 0.5 - Math.random());

        setLooksLikeQuestion(`Which of the following shapes looks like a ${randomShape}?`);
        setCorrectLooksLike(correct);
        setLooksLikeOptions(options);
        setSelected(null);
        setShowImage(false);
    };


    const generateNewFigure = () => {
        const newShapes: ShapeData[] = [];
        const target = getRandomShape();
        let currentTop = 20;
        let currentLeft = 20;

        for (let i = 0; i < shapeCount; i++) {
            const type = getRandomShape();
            const size = Math.floor(Math.random() * 30) + 20;
            newShapes.push({ type, top: currentTop, left: currentLeft, size });

            currentLeft += size + 10;
            if (currentLeft > 200) {
                currentLeft = 20;
                currentTop += 60;
            }
        }

        const count = newShapes.filter(shape => shape.type === target).length;
        const optionChoices = generateOptions(count);

        setShapes(newShapes);
        setTargetShape(target);
        setCorrectCount(count);
        setOptions(optionChoices);
        setSelected(null);
        setShowImage(false);
    };

    const generateRollSlide = () => {
        const type = ["roll", "slide", "both"][Math.floor(Math.random() * 3)] as
            | "roll"
            | "slide"
            | "both";

        const correctArray = type === "roll" ? rollOnlyImages : type === "slide" ? slideOnlyImages : bothImages;

        let wrongArray = [...rollOnlyImages, ...slideOnlyImages, ...bothImages].filter(
            img => img.answerType !== type
        );
        if (type === "roll") {
            wrongArray = [...slideOnlyImages].filter(
                img => img.answerType !== type
            );
        }

        if (type === "slide") {
            wrongArray = [...rollOnlyImages].filter(
                img => img.answerType !== type
            );
        }

        if (type === "both") {
            wrongArray = [...rollOnlyImages, ...slideOnlyImages, ...bothImages].filter(
                img => img.answerType !== type
            );
        }



        const correctOption = correctArray[Math.floor(Math.random() * correctArray.length)];
        const shuffled = [correctOption, ...wrongArray.sort(() => 0.5 - Math.random()).slice(0, 3)].sort(
            () => 0.5 - Math.random()
        );

        setRollSlideQuestion(
            type === "roll"
                ? "Which of the following object can roll but not slide?"
                : type === "slide"
                    ? "Which of the following object can slide but not roll?"
                    : "Which of the following object can roll and slide both?"
        );
        setCorrectRollSlide(correctOption.label);
        setRollSlideOptions(shuffled);
        setSelected(null);
        setShowImage(false);
    };

    const handleSelect = (selectedVal: any) => {
        setSelected(selectedVal);
        if (questionType === "looksLike") {
            if (selectedVal === correctLooksLike) {
                setIsCorrect(true);
                setShowImage(true);
                setTimeout(() => {
                    setShowImage(false);
                    resetQuestion();
                }, 2000);
            } else {
                setIsCorrect(false);
            }
        }
        else if (questionType === "figure") {
            if (selectedVal === correctCount) {
                setIsCorrect(true);
                setShowImage(true);
                setTimeout(() => {
                    setShowImage(false);
                    resetQuestion();
                }, 2000);
            } else {
                setIsCorrect(false);
            }
        } else {
            if (selectedVal === correctRollSlide) {
                setIsCorrect(true);
                setShowImage(true);
                setTimeout(() => {
                    setShowImage(false);
                    resetQuestion();
                }, 2000);
            } else {
                setIsCorrect(false);
            }
        }
    };

    const resetQuestion = () => {
        const pickType = getRandomQuestionType(); // "figure", "rollSlide", or "looksLike"
        setQuestionType(pickType);

        if (pickType === "figure") generateNewFigure();
        else if (pickType === "rollSlide") generateRollSlide();
        else generateLooksLike();
    };

    return (
        <>
            {!showImage ? (
                <div className="contentPart fillPreposition additionWord rankingTest" style={{ marginTop: "0" }}>
                    {questionType === "figure" && (
                        <>
                            <h3>How many {targetShape}s are there?</h3>
                            <div className="mb-5 figure-container">
                                {shapes.map((shape, idx) => {
                                    const { type, top, left, size } = shape;

                                    if (type === "triangle") {
                                        return (
                                            <svg
                                                key={idx}
                                                width={size}
                                                height={size}
                                                style={{ position: "absolute", top: `${top}px`, left: `${left}px` }}
                                            >
                                                <polygon
                                                    points={`${size / 2},0 0,${size} ${size},${size}`}
                                                    fill="transparent"
                                                    stroke="black"
                                                    strokeWidth="2"
                                                />
                                            </svg>
                                        );
                                    }

                                    if (type === "rectangle") {
                                        return (
                                            <div
                                                key={idx}
                                                className="shape rectangle"
                                                style={{
                                                    top: `${top}px`,
                                                    left: `${left}px`,
                                                    width: `${size * 2}px`,
                                                    height: `${size}px`,
                                                    border: "2px solid black",
                                                    position: "absolute",
                                                }}
                                            />
                                        );
                                    }

                                    const borderRadius = type === "circle" ? "50%" : "0%";

                                    return (
                                        <div
                                            key={idx}
                                            className={`shape ${type}`}
                                            style={{
                                                top: `${top}px`,
                                                left: `${left}px`,
                                                width: `${size}px`,
                                                height: `${size}px`,
                                                border: "2px solid black",
                                                position: "absolute",
                                                borderRadius,
                                            }}
                                        />
                                    );
                                })}
                            </div>

                            <IonRadioGroup className="mb-5 displayFlex" value={selected} onIonChange={e => handleSelect(e.detail.value)}>
                                {options.map((option, idx) => (
                                    <IonItem key={idx}>
                                        <IonLabel>{option}</IonLabel>
                                        <IonRadio slot="start" value={option} />
                                    </IonItem>
                                ))}
                            </IonRadioGroup>
                        </>
                    )}

                    {questionType === "rollslide" && (
                        <>
                            <h3>{rollSlideQuestion}</h3>
                            <div className="mb-5 displayFlex">
                                {rollSlideOptions.map((opt, idx) => (
                                    <div key={idx} className="image-container" onClick={e => handleSelect(opt.label)}>
                                        <img className="repeating"
                                            src={`${process.env.REACT_APP_API_URL ?? ""}${opt.src ?? "question-mark.png"}`}
                                            alt="pattern-img"
                                        />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {questionType === "looksLike" && (
                        <>
                            <h3>{looksLikeQuestion}</h3>
                            <div className="mb-5 displayFlex">
                                {looksLikeOptions.map((opt, idx) => (
                                    <div key={idx} className="image-container" onClick={() => handleSelect(opt.label)}>
                                        <img
                                            className="repeating"
                                            src={`${process.env.REACT_APP_API_URL ?? ""}${opt.src ?? "question-mark.png"}`}
                                            alt="shape-img"
                                        />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK ?? ""}
                        alt="Well Done"
                    />
                </div>
            )}
        </>
    );
};

export default CountShapesFigure;
